import { useMemo, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import deepequals from 'fast-deep-equal/es6/index'
import { Box, ComboboxList, ComboboxOption, ComboboxPopover, ComboboxSearchInput, Spinner, FloatingLabel, Combobox, InputWithFloatingLabel } from '@vp/swan'
import { useFilterOptions } from '~/app/features/Gallery/hooks/useFilterOptions'
import { selectFilters } from '~/app/store/filter'
import { REFINEMENT_DIMENSION } from 'shared/constants'
import { scrollUpToElement } from '~/app/utils/scrollToElement'
import { galleryHeaderId } from '~/app/features/Gallery/components/Header/ActionHeader/constants'
import { useTranslations } from '~/app/features/Gallery/hooks/useTranslations'
import { Highlight } from '~/app/components/Highlight'
import { useSuggestions } from '~/app/features/Gallery/components/Header/ActionHeader/components/Searchbox/hooks/useSuggestions'
import { buildRefinement, refinementAddAndRemove } from '~/app/store/refinement'
import { useRedirectToL0 } from '~/app/features/Gallery/hooks/RedirectToL0/useRedirectToL0'
import { ThunkDispatch } from '~/app/store/interface'
import type { State } from 'shared/types/gallery/state'

export interface PropTypes {
  autoFocus?: boolean
  isMobile?: boolean
}

export const SearchBox = ({ autoFocus, isMobile }: PropTypes) => {
  const dispatch = useDispatch<ThunkDispatch>()
  const localize = useTranslations()
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null)
  const wrapperRef = useCallback((node: HTMLDivElement | null) => {
    setPortalContainer(node)
  }, [])
  const redirectToL0 = useRedirectToL0()

  const colorFilter = useSelector(selectFilters, deepequals).find((f) => f.type.toUpperCase() === 'COLOR')
  const { options: colorOptions } = useFilterOptions(colorFilter as State.AttributeFilter)
  const colorDict = useMemo(
    () =>
      colorOptions.reduce(
        (accum, option) => ({
          ...accum,
          [option.title.toLocaleUpperCase()]: option.value,
        }),
        {} as Record<string, string>,
      ),
    [colorOptions],
  )

  const list = useSuggestions()

  const shouldShowLoading = list.items.length === 0 && list.isLoading
  const shouldShowPopover = list.isLoading || list.items.length > 0

  const handleInputChange = (filterText: string): void => {
    list.setFilterText(filterText)
  }

  const handleSearch = (search: string, isAutosuggest: boolean): void => {
    const searchTerms = search.split(' ')

    if (searchTerms.length === 1 && colorDict[search.toLocaleUpperCase()]) {
      const refinement = buildRefinement(colorDict[search.toLocaleUpperCase()], REFINEMENT_DIMENSION.ATTRIBUTE)

      if (redirectToL0([refinement], [])) {
        return
      }

      dispatch(refinementAddAndRemove([refinement]))
    } else {
      const isBlackFriday = search.toLocaleUpperCase().includes('BLACK FRIDAY')

      const refinementsToAdd = !isBlackFriday
        ? searchTerms.reduce((accum, term) => {
            if (colorDict[term.toLocaleUpperCase()]) {
              const refinement = buildRefinement(colorDict[term.toLocaleUpperCase()], REFINEMENT_DIMENSION.ATTRIBUTE)

              accum.push(refinement)
            }

            return accum
          }, [] as State.Refinement[])
        : []

      refinementsToAdd.push(buildRefinement(search, REFINEMENT_DIMENSION.KEYWORD, { isAutosuggest }))

      if (redirectToL0(refinementsToAdd, [])) {
        return
      }

      dispatch(refinementAddAndRemove(refinementsToAdd))
    }

    if (!isMobile) {
      scrollUpToElement(galleryHeaderId)
    }

    list.setFilterText('')
  }

  const handleSelect = (value: string | number): void => {
    if (value) {
      handleSearch(`${value}`, true)
    }
  }

  const handleSubmit = (event: React.SyntheticEvent): void => {
    event.preventDefault()

    if (list.filterText) {
      handleSearch(list.filterText, false)
    }
  }

  return (
    <div className="search-box" data-testid="search-box" ref={wrapperRef}>
      <form role="search" onSubmit={handleSubmit}>
        <InputWithFloatingLabel>
          <Combobox
            allowsCustomValue
            disableAutoFilter
            allowEmptyCollection={list.isLoading}
            aria-label={localize('HeaderHierarchySearchTemplates')}
            inputValue={list.filterText}
            items={list.items}
            onInputChange={handleInputChange}
            onSelectionChange={handleSelect}
          >
            <FloatingLabel>{localize('HeaderHierarchySearchTemplates')}</FloatingLabel>
            <ComboboxSearchInput
              accessibleTextForClearButton={localize('SearchBoxAccessibleTextForClearButton')}
              accessibleTextForSearchButton={localize('SearchBoxAccessibleTextForSearchButton')}
              autoFocus={autoFocus}
              className="search-box-input"
              data-testid="input-search-box"
              placeholder={localize('HeaderHierarchySearchTemplates')}
              title={localize('SearchBoxTextInputTitle')}
            />

            {shouldShowPopover && (
              <ComboboxPopover portalContainer={portalContainer ?? undefined}>
                <ComboboxList>
                  {({ val: value }) => (
                    <ComboboxOption key={value} textValue={value} data-testid="search-item">
                      <Highlight fullText={value} searchTerm={list.filterText} />
                    </ComboboxOption>
                  )}
                </ComboboxList>
                {shouldShowLoading && (
                  <Box pb="5" pt={0} px="4">
                    <Spinner showText accessibleText={localize('PhotoPersonalizationImageSelectionAssetLoadingLabel')} layout="horizontal" size="mini" />
                  </Box>
                )}
              </ComboboxPopover>
            )}
          </Combobox>
        </InputWithFloatingLabel>
      </form>
    </div>
  )
}

SearchBox.displayName = 'SearchBox'
